import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useReducedMotion from '../../hooks/useReducedMotion';
import './PageTransition.css';

/**
 * Prelazi između stranica iz dizajna.
 *
 * Dizajn ih izvodi tako što ubaci <iframe> sledeće stranice i pomera obe —
 * u SPA to ne važi jer nema druge stranice da se učita. Ovde isti utisak daje
 * jedan sloj preko svega: pokrije ekran, ispod njega se promeni ruta, pa se
 * skloni. Rezultat je isti pokret, bez drugog dokumenta.
 *
 * Dva oblika:
 *   grow  — pravougaonik dugmeta naraste preko celog ekrana (radijus 40 → 0).
 *           Traži pravougaonik elementa na koji je kliknuto.
 *   wipe  — sloj uđe zdesna, pa nastavi ulevo i otkrije novu stranicu.
 *
 * Uz „smanji kretanje" nema ničega — navigacija je trenutna.
 */

const TransitionContext = createContext(null);

const GROW_IN = 620;
const GROW_OUT = 420;
const WIPE_IN = 520;
const WIPE_OUT = 520;

export function PageTransitionProvider({ children }) {
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const [state, setState] = useState(null); // { kind, phase, rect }
  const busy = useRef(false);

  const go = useCallback((to, { kind = 'wipe', origin = null } = {}) => {
    if (reduced) { navigate(to); return; }
    // Drugi klik dok prelaz traje bi ostavio sloj zaglavljen preko sadržaja
    if (busy.current) return;
    busy.current = true;

    const rect = origin?.getBoundingClientRect?.();
    setState({
      kind,
      phase: 'in',
      rect: rect ? {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        // Stvarne mere umesto 100vw/100vh: `vw` uključuje traku za skrolovanje,
        // pa bi razmera bila netačna i sloj bi promašio ivicu ekrana.
        vw: document.documentElement.clientWidth,
        vh: document.documentElement.clientHeight,
      } : null,
    });

    const inMs = kind === 'grow' ? GROW_IN : WIPE_IN;
    const outMs = kind === 'grow' ? GROW_OUT : WIPE_OUT;

    window.setTimeout(() => {
      navigate(to);
      setState((s) => (s ? { ...s, phase: 'out' } : null));
      window.setTimeout(() => {
        setState(null);
        busy.current = false;
      }, outMs);
    }, inMs);
  }, [navigate, reduced]);

  return (
    <TransitionContext.Provider value={go}>
      {children}
      {state && (
        <div
          className={`ptrans ptrans--${state.kind} is-${state.phase}`}
          aria-hidden="true"
          style={state.rect ? {
            '--x': `${state.rect.left}px`,
            '--y': `${state.rect.top}px`,
            '--sx': state.rect.width / state.rect.vw,
            '--sy': state.rect.height / state.rect.vh,
          } : undefined}
        />
      )}
    </TransitionContext.Provider>
  );
}

/**
 * Vraća `go(to, { kind, origin })`.
 * `origin` je DOM element od kog kreće rast — obično e.currentTarget.
 */
export function useRouteTransition() {
  const ctx = useContext(TransitionContext);
  if (!ctx) throw new Error('useRouteTransition mora biti unutar PageTransitionProvider');
  return ctx;
}

/**
 * Link koji pri kliku pokreće prelaz umesto obične navigacije.
 * Ostaje pravi <a href> — srednji klik, „otvori u novoj kartici" i
 * pretraživači i dalje rade kako treba.
 */
export function TransitionLink({ to, kind = 'wipe', className, children, ...rest }) {
  const go = useRouteTransition();

  const onClick = (e) => {
    // Ne otimaj klik koji korisnik namerno šalje u novu karticu
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    e.currentTarget.classList.add('is-launching');
    go(to, { kind, origin: e.currentTarget });
  };

  return (
    <a href={to} className={className} onClick={onClick} {...rest}>
      {children}
    </a>
  );
}
